import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles } from "../../Services/ReactPDFService.jsx";

// Minimalist Pro Cover Letter Template - Clean design with teal accent matching the resume
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 50,
  },
  // Header with teal accent bar
  headerContainer: {
    marginHorizontal: -50,
    marginTop: -40,
    paddingHorizontal: 50,
    borderTopWidth: 6,
    borderTopColor: "#0d9488",
    paddingTop: 30,
    paddingBottom: 20,
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    alignItems: "flex-end",
  },
  name: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  title: {
    fontSize: 11,
    color: "#0d9488",
    fontWeight: 500,
  },
  // Contact with icons
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  contactIcon: {
    width: 10,
    height: 10,
    marginRight: 6,
  },
  contactText: {
    fontSize: 9,
    color: "#6b7280",
  },
  // Body content
  bodyText: {
    fontSize: 11,
    lineHeight: 1.8,
    color: "#374151",
    textAlign: "justify",
    marginBottom: 16,
  },
});

// Contact icon paths
const contactIcons = {
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
};

const MinimalistProCoverLetter = ({ coverLetterData, customStyles = {} }) => {
  const { header, generatedContent } = coverLetterData || {};
  const { name, email, phone, location } = header || {};

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { titleColor } = customizations;
    const customStyleOverrides = {};

    if (titleColor) {
      // Header accent bar color
      customStyleOverrides.headerContainer = {
        ...baseStyles.headerContainer,
        borderTopColor: titleColor,
      };
      customStyleOverrides.name = {
        ...baseStyles.name,
        color: titleColor,
      };
      customStyleOverrides.title = {
        ...baseStyles.title,
        color: titleColor,
      };
    }

    return { ...baseStyles, ...customStyleOverrides };
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Parse the generated content into paragraphs
  const paragraphs = generatedContent
    ? generatedContent.split("\n").filter((p) => p.trim().length > 0)
    : ["[Your cover letter content will appear here]"];

  // Render contact icon
  const renderContactIcon = (iconPath) => (
    <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#9ca3af" />
    </Svg>
  );

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page}>
        {/* Header with accent */}
        <View style={mergedStyles.headerContainer}>
          <View style={mergedStyles.headerContent}>
            <View style={mergedStyles.headerLeft}>
              <Text style={mergedStyles.name}>{name || "[Your Name]"}</Text>
            </View>
            <View style={mergedStyles.headerRight}>
              {/* Email */}
              {email && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.email)}
                  <Text style={mergedStyles.contactText}>{email}</Text>
                </View>
              )}
              {/* Phone */}
              {phone && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.phone)}
                  <Text style={mergedStyles.contactText}>{phone}</Text>
                </View>
              )}
              {/* Location */}
              {location && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.location)}
                  <Text style={mergedStyles.contactText}>{location}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Body Content */}
        {paragraphs.map((paragraph, index) => (
          <Text key={index} style={mergedStyles.bodyText}>
            {paragraph}
          </Text>
        ))}
      </Page>
    </Document>
  );
};

export default MinimalistProCoverLetter;
